import Icon from '@/components/ui/AppIcon';

interface ChemistryProtocol {
  id: number;
  stage: string;
  chemistry: string;
  purpose: string;
  parameters: string[];
  icon: string;
}

const protocols: ChemistryProtocol[] = [
  {
    id: 1,
    stage: 'Alkaline Cleaning',
    chemistry: 'NaOH + Surfactants',
    purpose: 'Removes oils, greases, and organic contaminants from substrate surface',
    parameters: [
      'Concentration: 2-5% by volume',
      'Temperature: 140-160°F',
      'Immersion: 3-5 minutes',
      'pH: 11-13'
    ],
    icon: 'BeakerIcon'
  },
  {
    id: 2,
    stage: 'Acid Rinse',
    chemistry: 'H₃PO₄ Phosphoric Acid',
    purpose: 'Neutralizes alkaline residue and etches surface for improved adhesion',
    parameters: [
      'Concentration: 0.5-1.5%',
      'Temperature: 100-120°F',
      'Immersion: 30-60 seconds',
      'pH: 2-3'
    ],
    icon: 'DropletIcon'
  },
  {
    id: 3,
    stage: 'Zirconium Conversion',
    chemistry: 'Zr(OH)₄ + HF Complex',
    purpose: 'Creates nano-ceramic conversion layer for superior corrosion protection',
    parameters: [
      'Concentration: 200-400 ppm Zr',
      'Temperature: 120-140°F',
      'Immersion: 90-120 seconds',
      'pH: 4.5-5.5'
    ],
    icon: 'ShieldCheckIcon'
  },
  {
    id: 4,
    stage: 'DI Water Seal',
    chemistry: 'Deionized H₂O',
    purpose: 'Final rinse removes residual chemistry and prevents water spotting',
    parameters: [
      'Conductivity: <10 μS/cm',
      'Temperature: Ambient',
      'Cascade flow: 2-stage',
      'pH: 6.5-7.5'
    ],
    icon: 'SparklesIcon'
  }
];

const ChemistryProtocols = () => {
  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 rounded-full mb-4">
            <Icon name="BeakerIcon" size={20} variant="outline" className="text-success" />
            <span className="text-sm font-cta font-semibold text-success">
              Scientific Rigor
            </span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mb-4">
            Pre-Treatment Chemistry Protocols
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Our zirconium-based conversion coating system delivers superior adhesion and corrosion resistance through precise chemical control.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {protocols.map((protocol) => (
            <div
              key={protocol.id}
              className="bg-card rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-base border border-border"
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                  <Icon name={protocol.icon as any} size={24} variant="outline" className="text-secondary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-headline font-bold text-foreground mb-1">
                    {protocol.stage}
                  </h3>
                  <div className="text-sm font-accent text-accent font-medium">
                    {protocol.chemistry}
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                {protocol.purpose}
              </p>

              <div className="space-y-2 pt-4 border-t border-border">
                <div className="text-xs font-cta font-semibold text-foreground uppercase tracking-wide mb-2">
                  Process Parameters
                </div>
                {protocol.parameters.map((param, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{param}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-accent/5 border border-accent/20 rounded-lg p-6">
          <div className="flex items-start space-x-4">
            <Icon name="ExclamationTriangleIcon" size={24} variant="outline" className="text-accent flex-shrink-0" />
            <div>
              <h4 className="text-lg font-headline font-bold text-foreground mb-2">
                Chemistry Control & Monitoring
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All pre-treatment baths are monitored continuously with automated titration systems. Daily lab analysis validates concentration, pH, and contamination levels. Bath chemistry is adjusted or replaced based on strict performance criteria to maintain optimal coating adhesion and corrosion resistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChemistryProtocols;